import React, { useEffect, useState } from "react";
import ProposalApi from "../../api/proposalApi";
import axios from "../../api/axiosinstance";
import { showSuccess, showError } from "../../utils/toastUtils";
import PackageApi from "../../api/PackageApi";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";

function CreateProposal() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [searchParams] = useSearchParams();
const packageId = searchParams.get("packageId");

  const [formData, setFormData] = useState({
    // LEFT COLUMN
    subject: "",
    related: id,
    date: new Date().toISOString().split("T")[0],
    openTill: "",
    currency: "USD",
    discountType: "No discount",
    tags: "",
    allowComments: true,

    // RIGHT COLUMN
    status: "Draft",
    assignedTo: "",
    clientName: "",
    address: "",
    city: "",
    state: "",
    country: "India",
    zip: "",
    email: "",
    phone: "",

    items: [],
    subTotal: 0,
    discount: 0,
    adjustment: 0,
    total: 0,
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get("users/list");
      if (res.code === 200) {
        setUsers(res.data || []);
      }
    } catch (err) {
      console.log(err);
    }
  };

  /* ================= LOAD LEAD ================= */

  useEffect(() => {
  if (id) {
    fetchLead();
  }
}, [id]);

const fetchLead = async () => {
  try {
    const res = await axios.get(`leadFinals/${id}`);

    if (res.code === 200) {
      const data = res.data;

      setFormData((prev) => ({
        ...prev,
        clientName: data.name || "",
        email: data.email || "",
        phone: data.phone || "",
        address: data.fullAddress || "",
        state: data.state || "",
        city: data.district || "",
        country: data.country || "India",
        zip: data.pincode || "",
        assignedTo: data.assignedTo?._id || "",
      }));
    }
  } catch (err) {
    console.log("Lead Fetch Error:", err);

    if (err?.message) {
      showError(err.message);
    } else {
      showError("Failed to load lead");
    }
  }
};

useEffect(() => {
  if (packageId) {
    fetchPackageItems();
  }
}, [packageId]);

const fetchPackageItems = async () => {
  try {
    const res = await PackageApi.list();

    if (res.code === 200) {
      const selected = res.data.find(p => p._id === packageId);

      if (selected) {
        const items = selected.modules.map(m => ({
          title: m.moduleName,
          description: m.description || "",
          qty: 1,
          rate: Number(m.price || 0),
          tax: 0,
          amount: Number(m.price || 0),
          optional: false
        }));

        const subTotal = items.reduce((sum, i) => sum + i.amount, 0);

        setFormData(prev => ({
          ...prev,
          subject: selected.packageName + " Proposal", // 🔥 auto subject
          items,
          subTotal,
          total: subTotal
        }));
      }
    }
  } catch (err) {
    console.log("Package fetch error");
  }
};

  /* ================= HANDLE CHANGE ================= */

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  /* ================= SAVE ================= */

  const handleSave = async () => {
    // Basic validation
    if (!formData.subject) {
      showError("Subject is required");
      return;
    }

    if (!formData.clientName) {
      showError("Client name is required");
      return;
    }

    if (!formData.email) {
      showError("Email is required");
      return;
    }

    if (formData.items.length === 0) {
      showError("Please add at least one item");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        leadId: id,
        subject: formData.subject,
        status: formData.status,
        assignedTo: formData.assignedTo || undefined,

        clientDetails: {
          name: formData.clientName,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          state: formData.state,
          city: formData.city,
          country: formData.country,
          zip: formData.zip,
        },

        meta: {
          related: formData.related,
          date: formData.date,
          openTill: formData.openTill,
          currency: formData.currency,
          discountType: formData.discountType,
          tags: formData.tags,
          allowComments: formData.allowComments,
        },

        items: formData.items,
        subTotal: formData.subTotal,
        discount: formData.discount,
        adjustment: formData.adjustment,
        total: formData.total,
      };

      const res = await ProposalApi.add(payload);
      if (res.code === 200) {
        showSuccess("Proposal saved successfully");

        // 🔥 Auto Download PDF
        navigate(`/proposals/${res.data._id}`);

        // Reset form
        setFormData((prev) => ({
          ...prev,
          subject: "",
          items: [],
          subTotal: 0,
          discount: 0,
          adjustment: 0,
          total: 0,
        }));

        // Optional redirect after save
        // navigate(`/lead-details/${id}`);
      }
    } catch (err) {
      console.log("Full error:", err);

      if (err?.message) {
        showError(err.message);
      } else if (err?.data?.message) {
        showError(err.data.message);
      } else {
        showError("Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  /* ================= ITEMS LOGIC ================= */

  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          title: "",
          description: "",
          qty: 1,
          rate: 0,
          tax: 0,
          amount: 0,
          optional: false,
        },
      ],
    }));
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];

    updatedItems[index][field] = value;

    const qty = Number(updatedItems[index].qty || 0);
    const rate = Number(updatedItems[index].rate || 0);
    const taxPercent = Number(updatedItems[index].tax || 0);

    const baseAmount = qty * rate;
    const taxAmount = (baseAmount * taxPercent) / 100;

    updatedItems[index].amount = baseAmount + taxAmount;

    const subTotal = updatedItems.reduce(
      (sum, item) => sum + Number(item.amount || 0),
      0,
    );

    setFormData((prev) => ({
      ...prev,
      items: updatedItems,
      subTotal,
      total: subTotal - prev.discount + prev.adjustment,
    }));
  };

  const predefinedItems = [
    {
      name: "Website Basic Package",
      description: "Basic website with 5 pages",
      rate: 10000,
    },
    {
      name: "E-commerce Package",
      description: "Full ecommerce website with payment gateway",
      rate: 25000,
    },
    {
      name: "SEO Package",
      description: "Complete SEO optimization",
      rate: 8000,
    },
  ];

  return (
    <div className="proposal-wrapper">
      <div className="proposal-card">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 style={{ fontWeight: 600 }}>Create Proposal</h4>
          <button className="btn-theme" onClick={handleSave} disabled={loading}>
            {loading ? "Saving..." : "Save Proposal"}
          </button>
        </div>

        <div className="row">
          {/* LEFT SIDE */}
          <div className="col-md-6">
            <div className="section-title">Proposal Details</div>

            <div className="mb-3">
              <label className="form-label ">Subject</label>

              <label className="form-label text-danger"> *</label>
              <input
                type="text"
                name="subject"
                className="form-control"
                value={formData.subject}
                onChange={handleChange}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Related Lead</label>
              <input
                className="form-control"
                value={formData.related}
                disabled
              />
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label>Date</label>
                <input
                  type="date"
                  name="date"
                  className="form-control"
                  value={formData.date}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-6 mb-3">
                <label>Open Till</label>
                <input
                  type="date"
                  name="openTill"
                  className="form-control"
                  value={formData.openTill}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label text-danger">*</label>
                <label className="form-label"> Currency</label>
                <select
                  name="currency"
                  className="form-control"
                  value={formData.currency}
                  onChange={handleChange}
                >
                  <option>USD $</option>
                  <option>INR ₹</option>
                  <option>EUR €</option>
                </select>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Discount Type</label>
                <select
                  name="discountType"
                  className="form-control"
                  value={formData.discountType}
                  onChange={handleChange}
                >
                  <option>No discount</option>
                  <option>Percentage</option>
                  <option>Flat</option>
                </select>
              </div>
            </div>
            <div className="section-title">Status & Assignment</div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Status</label>
                <select
                  name="status"
                  className="form-control"
                  value={formData.status}
                  onChange={handleChange}
                >
                  <option>Draft</option>
                  <option>Sent</option>
                  <option>Revised</option>
                  <option>Closed</option>
                </select>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Assigned</label>
                <select
                  name="assignedTo"
                  className="form-control"
                  value={formData.assignedTo}
                  onChange={handleChange}
                >
                  <option value="">Select User</option>
                  {users.map((user) => (
                    <option key={user._id} value={user._id}>
                      {user.name} ({user.email})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* RIGHT SIDE */}
          {/* ================= RIGHT COLUMN ================= */}
          <div className="col-md-6">
            <div className="section-title">Client Details</div>

            <div className="mb-3">
              <label className="form-label"> To</label>
              <label className="form-label text-danger">*</label>

              <input
                type="text"
                name="clientName"
                className="form-control"
                value={formData.clientName}
                onChange={handleChange}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Address</label>
              <textarea
                name="address"
                className="form-control"
                rows="3"
                value={formData.address}
                onChange={handleChange}
              />
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">City</label>
                <input
                  name="city"
                  className="form-control"
                  value={formData.city}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">State</label>
                <input
                  name="state"
                  className="form-control"
                  value={formData.state}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Country</label>
                <select
                  name="country"
                  className="form-control"
                  value={formData.country}
                  onChange={handleChange}
                >
                  <option>India</option>
                  <option>USA</option>
                  <option>UK</option>
                </select>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Zip Code</label>
                <input
                  name="zip"
                  className="form-control"
                  value={formData.zip}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  name="email"
                  className="form-control"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  className="form-control"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>
        </div>

        <hr className="my-4" />

        {/* ITEMS SECTION */}
        {/* <div className="section-title">Proposal Items</div>

        

        {formData.items.map((item, index) => (
          <div key={index} className="item-card">
            <div className="row">
              <div className="col-md-4">
                <label>Description</label>
                <textarea
                  className="form-control"
                  value={item.title}
                  onChange={(e) =>
                    handleItemChange(index, "title", e.target.value)
                  }
                />
              </div>

              <div className="col-md-2">
                <label>Qty</label>
                <input
                  type="number"
                  className="form-control"
                  value={item.qty}
                  onChange={(e) =>
                    handleItemChange(index, "qty", e.target.value)
                  }
                />
              </div>

              <div className="col-md-2">
                <label>Rate</label>
                <input
                  type="number"
                  className="form-control"
                  value={item.rate}
                  onChange={(e) =>
                    handleItemChange(index, "rate", e.target.value)
                  }
                />
              </div>

              <div className="col-md-2">
                <label>Tax %</label>
                <input
                  type="number"
                  className="form-control"
                  value={item.tax}
                  onChange={(e) =>
                    handleItemChange(index, "tax", e.target.value)
                  }
                />
              </div>

              <div className="col-md-2">
                <label>Amount</label>
                <input
                  className="form-control"
                  value={item.amount?.toFixed(2)}
                  readOnly
                />
              </div>
            </div>
          </div>
        ))} */}

        <div className="section-title">Proposal Items</div>

        <div className="row mb-3">
          <div className="col-md-6">
            <label>Add Item</label>
            <select
              className="form-control"
              onChange={(e) => {
                const selected = predefinedItems.find(
                  (item) => item.name === e.target.value,
                );
                if (!selected) return;

                const newItem = {
                  title: selected.name,
                  description: selected.description,
                  qty: 1,
                  rate: selected.rate,
                  tax: 0,
                  amount: selected.rate,
                  optional: false,
                };

                const updatedItems = [...formData.items, newItem];

                const subTotal = updatedItems.reduce(
                  (sum, item) => sum + item.amount,
                  0,
                );

                setFormData((prev) => ({
                  ...prev,
                  items: updatedItems,
                  subTotal,
                  total: subTotal - prev.discount + prev.adjustment,
                }));
              }}
            >
              <option value="">Select Item</option>
              {predefinedItems.map((item, index) => (
                <option key={index} value={item.name}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {formData.items.map((item, index) => (
          <div key={index} className="item-card">
            <div className="row">
              <div className="col-md-4">
                <label>Short Description</label>
                <input
                  className="form-control"
                  value={item.title}
                  onChange={(e) =>
                    handleItemChange(index, "title", e.target.value)
                  }
                />
              </div>

              <div className="col-md-4">
                <label>Long Description</label>
                <textarea
                  className="form-control"
                  value={item.description}
                  onChange={(e) =>
                    handleItemChange(index, "description", e.target.value)
                  }
                />
              </div>

              <div className="col-md-1">
                <label>Qty</label>
                <input
                  type="number"
                  className="form-control"
                  value={item.qty}
                  onChange={(e) =>
                    handleItemChange(index, "qty", e.target.value)
                  }
                />
              </div>

              <div className="col-md-1">
                <label>Rate</label>
                <input
                  type="number"
                  className="form-control"
                  value={item.rate}
                  onChange={(e) =>
                    handleItemChange(index, "rate", e.target.value)
                  }
                />
              </div>

              <div className="col-md-1">
                <label>Tax</label>
                <input
                  type="number"
                  className="form-control"
                  value={item.tax}
                  onChange={(e) =>
                    handleItemChange(index, "tax", e.target.value)
                  }
                />
              </div>

              <div className="col-md-1">
                <label>Amount</label>
                <input
                  className="form-control"
                  value={item.amount?.toFixed(2)}
                  readOnly
                />
              </div>
            </div>
          </div>
        ))}

        <button className="btn-outline-theme mb-4" onClick={addItem}>
          + Add Item
        </button>

        {/* TOTAL SECTION */}
        <div className="total-box text-start">
          <p>Sub Total: ₹ {formData.subTotal.toFixed(2)}</p>

          <p>
            Discount:
            <input
              type="number"
              className="form-control d-inline-block w-auto ms-2"
              value={formData.discount}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  discount: Number(e.target.value),
                  total:
                    prev.subTotal - Number(e.target.value) + prev.adjustment,
                }))
              }
            />
          </p>

          <p>
            Adjustment:
            <input
              type="number"
              className="form-control d-inline-block w-auto ms-2"
              value={formData.adjustment}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  adjustment: Number(e.target.value),
                  total: prev.subTotal - prev.discount + Number(e.target.value),
                }))
              }
            />
          </p>

          <div className="total-highlight">
            Total: ₹ {formData.total.toFixed(2)}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateProposal;
